import React, { createContext, useContext, useEffect, useState } from 'react';
import { type User, onAuthStateChanged, signInWithPopup, GoogleAuthProvider, signOut, getAdditionalUserInfo } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../services/firebase';
import { grantInitialCredits } from '../services/api';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  credits: number | null;
  signInWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [credits, setCredits] = useState<number | null>(null);

  useEffect(() => {
    // Set a timeout to ensure we don't get stuck in loading state if Firebase hangs
    const timeoutId = setTimeout(() => {
        if (loading) {
            console.warn("Auth listener timed out, defaulting to unauthenticated");
            setLoading(false);
        }
    }, 2000);

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      console.log("Auth state changed:", user);
      setUser(user);
      setLoading(false);
      clearTimeout(timeoutId);
    }, (error) => {
      console.error("Auth subscription error:", error);
      setLoading(false);
      clearTimeout(timeoutId);
    });
    
    return () => {
        unsubscribe();
        clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    if (!user) {
      setCredits(null);
      return;
    }

    const userDocRef = doc(db, 'users', user.uid);
    const unsubscribe = onSnapshot(userDocRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setCredits(typeof data.creditBalance === 'number' ? data.creditBalance : 0);
      } else {
        setCredits(0);
      }
    }, (error) => {
      console.error("Error fetching user credits:", error);
    });

    return () => unsubscribe();
  }, [user]);

  const signInWithGoogle = async () => {
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      
      // Check if new user and grant credits
      const details = getAdditionalUserInfo(result);
      if (details?.isNewUser) {
        console.log("New user detected, granting initial credits...");
        try {
          await grantInitialCredits();
        } catch (e) {
          console.error("Failed to grant initial credits", e);
        }
      }
    } catch (error) {
      console.error("Sign in error:", error);
      throw error;
    }
  };

  const logout = async () => {
    await signOut(auth);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-black"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, loading, credits, signInWithGoogle, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};


